/**
 * useTranscription Hook
 *
 * Provides audio transcription with local-first, API-fallback architecture:
 * 1. Attempts local transcription (browser-based Whisper)
 * 2. Falls back to API if local fails or unavailable
 *
 * Features:
 * - Zero-latency offline transcription when local works
 * - Automatic fallback to reliable API transcription
 * - Progress tracking for model downloads
 * - Model caching in IndexedDB (persists across sessions)
 */

import { useState, useCallback, useRef } from 'react';
import {
  loadWhisperModel,
  transcribeLocal,
  isWhisperSupported,
  WhisperModelSize,
} from '../lib/whisper-local';
import { transcribeAudio } from '../api/transcription';

export interface UseTranscriptionOptions {
  /** ISO-639-1 language code (e.g., 'en', 'es', 'fr') */
  language?: string;
  /** Whisper model size (tiny/base/small) */
  modelSize?: WhisperModelSize;
  /** Force API-only transcription (skip local) */
  forceApi?: boolean;
  /** Vocabulary hint to help with commonly misheard words */
  prompt?: string;
}

export interface UseTranscriptionReturn {
  /** Transcribe audio blob to text */
  transcribe: (audioBlob: Blob) => Promise<string>;
  /** True when loading model or transcribing */
  isLoading: boolean;
  /** True when local Whisper model is loaded */
  isModelLoaded: boolean;
  /** Model download progress (0-100) */
  progress: number;
  /** Error message if transcription failed */
  error: string | null;
  /** Method used for last transcription ('local' | 'api' | null) */
  method: 'local' | 'api' | null;
}

/**
 * Hook for audio transcription with local-first, API-fallback strategy
 *
 * @param options - Optional configuration
 * @returns Transcription interface
 */
export function useTranscription(
  options: UseTranscriptionOptions = {}
): UseTranscriptionReturn {
  const { language, modelSize = 'tiny', forceApi = false, prompt } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<'local' | 'api' | null>(null);

  // Track if model loading was attempted
  const modelLoadAttempted = useRef(false);

  /**
   * Transcribe audio using local-first, API-fallback strategy
   */
  const transcribe = useCallback(
    async (audioBlob: Blob): Promise<string> => {
      setIsLoading(true);
      setError(null);
      setMethod(null);

      try {
        // Skip local if forced to use API
        if (forceApi) {
          return await transcribeViaApi(audioBlob);
        }

        // Check if local transcription is supported
        if (!isWhisperSupported()) {
          console.log('[Transcription] Local Whisper not supported, using API');
          return await transcribeViaApi(audioBlob);
        }

        // Try local transcription
        try {
          return await transcribeViaLocal(audioBlob);
        } catch (localError) {
          console.warn('[Transcription] Local failed, falling back to API:', localError);
          // Fall back to API
          return await transcribeViaApi(audioBlob);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Transcription failed';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [forceApi, language, modelSize, prompt]
  );

  /**
   * Transcribe using local Whisper model
   */
  const transcribeViaLocal = async (audioBlob: Blob): Promise<string> => {
    // Load model if not already loaded
    if (!isModelLoaded && !modelLoadAttempted.current) {
      modelLoadAttempted.current = true;

      await loadWhisperModel(modelSize, (progressValue) => {
        setProgress(progressValue);
      });

      setIsModelLoaded(true);
      setProgress(100);
    }

    // Transcribe locally
    const options = { language, prompt };
    const text = await transcribeLocal(audioBlob, options);
    setMethod('local');
    return text;
  };

  /**
   * Transcribe using API
   */
  const transcribeViaApi = async (audioBlob: Blob): Promise<string> => {
    const result = await transcribeAudio(audioBlob, { language, prompt });
    setMethod('api');
    return result.text;
  };

  return {
    transcribe,
    isLoading,
    isModelLoaded,
    progress,
    error,
    method,
  };
}
